import React, { useMemo } from 'react';
import styles from './DebtProducts.module.scss';
import useLegacyBusinessDataFetch from '../DataRoom/useLegacyBusinessDataFetch';
import { Button } from '../../common/_custom';
import {
    caretRight,
    ctaGrey,
    debtProdFilters,
    debtProductsEmpty,
    editProfileIcon,
    NOT_ALLOWED,
    PURPLE_CROSS,
} from '../../assets/hostedassets';
import LoadingSpinner from '../../common/_custom/LoadingSpinner/LoadingSpinner';
import ManageDealDrawer from '../../common/ManageDealDrawer/ManageDealDrawer';
import { setDealDrawer } from '../../store/investor/action';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import clsx from 'clsx';
import ProbabilityBar from './components/ProbabilityBar/ProbabilityBar';
import probabilityBarStyles from './components/ProbabilityBar/ProbabilityBar.module.scss';
import DebtProductOverview from './components/DebtProductOverview/DebtProductOverview';
import Tooltip from '../../common/_custom/Tooltip/Tooltip';
import { DebtProductRowData } from './debtProductTypes';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useDebtProductLogic from './useDebtProductLogic';
import { USER_TYPE } from '../../utils/constants/user';
import { isEmpty } from '../../utils/utils';
import AIText from '../../common/AIText/AIText';
import { setDebtProductsFilters } from '../../store/company/action';
import { toggleProductExpand } from '../../store/debtProducts/action';
import DealSuccessModal from '../../common/DealApplicationDrawer/Components/DealSuccessModal/DealSuccessModal';
import SecurityModal from './components/SecurityModal/SecurityModal';
import AskAicaV2 from '../Dashboard/DashboardModules/Overview/components/AskAicaV2/AskAicaV2';
import { useVisibilityConfig } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import MandateDropdown from './components/MandateDropdown/MandateDropdown';
import MandateSharingDrawer from './components/MandateSharingDrawer/MandateSharingDrawer';
import ShareWithLenderModal from './components/ShareWithLenderModal/ShareWithLenderModal';
import { getTooltipContent } from './DebtProductsUtil';
import {
    ButtonText,
    DEAL_SHARED_FOR_ANOTHER_PRODUCT,
    DealPayloadScenario,
    ENTITY_TYPE_KEYS,
    getEditableFieldConfig,
    NAVIGATION_CONSTANTS,
    PRODUCT_TERMS_KEYS,
} from './DebtProductsConstants';
import { DATA_SOURCE_TYPE } from '../../common/ManageDealDrawer/DealDrawerConstants';
import MandateAggregatedTermsComponent from './components/MandateAggregatedTerms/MandateAggregatedTerms';
import mandateTermsStyles from './components/MandateAggregatedTerms/MandateAggregatedTerms.module.scss';
import { NumberCellRenderer } from '../../common/_custom/DataTable/components/CellRenderers/NumberCellRenderer/NumberCellRenderer';
import { DataTableColumnType } from '../../common/_custom/DataTable/DataTable.types';
import UnsavedChangesModal from './components/UnsavedChangesModal/UnsavedChangesModal';

function DebtProducts() {
    const dispatch = useAppDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const { selectedCompany } = useLegacyBusinessDataFetch();
    const header = document.getElementById('header') ?? document.createElement('div');
    const {
        loading, //component loading state
        noPecExists, //true if there are no existing PECs
        companyMetricsDataExists,
        companyMetricsData,
        metricsMetadata,
        showDetailOverview, //used to store row data for opening modal
        debtProductsData, //data used to render table
        showEmptyScreen, //boolean to show empty screen
        dealDrawerState,
        setShowDetailOverview,
        fetchPECDataAndDebtProductsData,
        fetchCompanyMetrics,
        updateProductTerms,
        updateLenderTerms,
        dealPayload,
        saveSelection,
        dealId,
        savingDraft,
        fetchDebtProductsData,
        //MIXPANEL
        registerMixpanelForRowExpand,
        registerMixpanelForUserHelpClick,
        registerMixpanelForEditProfileClicked,
        showFilters,
        renderDealActionButton,
        renderClearOrDeleteButton,
        dealStatus,
        draftDealsData,
        dealStatusConfig,
        selectedMandate,
        handleMandateSelection,
        percentageCompletion,
        getCheckboxState,
        countSelectedLenderCount,
        aggregatedMandateTerms,
        isMandateSharingDrawerOpen,
        setIsMandateSharingDrawerOpen,
        isShareWithLenderModalOpen,
        setIsShareWithLenderModalOpen,
        productTerms,
        lenderTerms,
        dealTerms,
        updateEndUseOfCapital,
        addDealNote,
        updateDealDocuments,
        selectedUsers,
        updateSelectedUsers,
        updateEditableField,
        isRowCalculating,
        isTransitioning,
        //Share success modal
        showShareSuccessModal,
        setShowShareSuccessModal,
        //Security modal
        showSecurityModal,
        setShowSecurityModal,
        updateSecurityOffered,
        //Probability tooltip
        getProbabilityTooltip,
        // Unsaved changes modal
        showUnsavedChangesModal,
        handleActionWithUnsavedCheck,
        handleCloseWithoutSaving,
        handleSaveChanges,
        handleCloseUnsavedModal,
    } = useDebtProductLogic({ params, selectedCompany });

    const { isVisible } = useVisibilityConfig();

    const user = useAppSelector((state) => state.user);
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const debtFilters = useAppSelector((state) => state.company.debtProductsFilters);
    const isCompanySide = user.userData.accountType === USER_TYPE.COMPANY || location.pathname.includes('company');
    const showSuccessModal = useAppSelector((state) => state.investor.dealApplicationDrawer.showSuccessModal);
    const assesseeOrgId = params.id;
    const lastUpdatedInfo = useMemo(() => {
        const { userName } = metricsMetadata || {};
        const { dataSource, lastUpdatedAt } = companyMetricsData?.borrowerFinancials || {};
        if (!userName || !dataSource) return null;
        const date = lastUpdatedAt
            ? new Date(lastUpdatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
            : '';
        const dataType = dataSource === DATA_SOURCE_TYPE.DISCUSSION ? 'discussion data' : 'actual data';
        return {
            prefix: `Last updated by ${userName} on ${date}`,
            highlight: `basis ${dataType}`,
        };
    }, [
        metricsMetadata?.userName,
        companyMetricsData?.borrowerFinancials?.dataSource,
        companyMetricsData?.borrowerFinancials?.lastUpdatedAt,
    ]);

    const renderEmptyScreen = () => {
        return (
            <div className={styles.Empty}>
                <img src={debtProductsEmpty} alt={''} height={'100px'} />
                {noPecExists ? (
                    <div className={styles.Text}>
                        {/*<span className={styles.Link} onClick={openCreatePECDrawer}>*/}
                        {/*    Add PEC*/}
                        {/*</span>*/}
                        &nbsp;Please set investment preferences first
                    </div>
                ) : companyMetricsDataExists ? (
                    <></>
                ) : (
                    isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.EDIT_PROFILE) && (
                        <div className={styles.Text}>
                            <span
                                className={styles.Link}
                                onClick={() => {
                                    dispatch(
                                        setDealDrawer({
                                            open: true,
                                            drawerLabel: 'Edit Profile',
                                            companyName: selectedCompany?.orgName,
                                        }),
                                    );
                                    registerMixpanelForEditProfileClicked('Empty Screen');
                                }}
                            >
                                Edit Profile
                            </span>
                            &nbsp;to view products with probability
                        </div>
                    )
                )}
            </div>
        );
    };

    const showLoader = () => (
        <div className={styles.Loader}>
            <LoadingSpinner color="#6021B3" height={'40px'} />
            <div className={styles.Text}>{`Fetching ${debtProductsData.visibilityStates.debtProductName}`}</div>
        </div>
    );

    const renderRowItem = (headerId, rowData: DebtProductRowData, isParentRow = false) => {
        const lenderIsShared =
            !isParentRow && rowData?.lenderId && rowData?.productId && lenderTerms[rowData.lenderId]?.[rowData.productId]?.isShared;
        const isLenderStrikethrough = lenderIsShared === -1;
        const isLenderGrayed = lenderIsShared !== undefined && lenderIsShared !== 0;
        const isFieldDisabled = isParentRow
            ? productTerms[rowData.productId]?.isShared !== undefined && productTerms[rowData.productId]?.isShared !== 0
            : rowData.lenderId &&
              lenderTerms[rowData.lenderId]?.[rowData.productId]?.isShared !== undefined &&
              lenderTerms[rowData.lenderId]?.[rowData.productId]?.isShared !== 0;

        if (headerId === ENTITY_TYPE_KEYS.CARET && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
            return null;
        }

        if (headerId === ENTITY_TYPE_KEYS.SELECT) {
            if (
                (isParentRow && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT)) ||
                (!isParentRow && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER))
            ) {
                return null;
            }
        }

        let renderComponent = <></>;
        switch (headerId) {
            case ENTITY_TYPE_KEYS.PRODUCT:
                renderComponent = (
                    <div
                        className={clsx(styles.Product, {
                            [styles.ProductSub]: !isParentRow,
                        })}
                    >
                        {isLenderStrikethrough ? (
                            <Tooltip content={DEAL_SHARED_FOR_ANOTHER_PRODUCT} direction="top" fitContent usePortal={true}>
                                <span className={clsx(styles.Strikethrough, styles.GrayedText)}>
                                    {(isParentRow ? rowData?.[headerId] : rowData?.['lenderName']) || '-'}
                                </span>
                            </Tooltip>
                        ) : (
                            <span
                                className={clsx({
                                    [styles.GrayedText]: !isParentRow && isLenderGrayed,
                                })}
                            >
                                {(isParentRow ? rowData?.[headerId] : rowData?.['lenderName']) || '-'}
                            </span>
                        )}
                        {!isParentRow && rowData?.isNegativeLender && (
                            <img src={NOT_ALLOWED} alt="na" height={'16px'} className={styles.NotAllowedIcon} />
                        )}
                        {isParentRow && !!rowData?.lenders?.length && (
                            <div className={styles.Count}>
                                {countSelectedLenderCount(rowData)}/{rowData?.lenders?.length}
                            </div>
                        )}
                    </div>
                );
                break;
            case ENTITY_TYPE_KEYS.SELECT:
                if (
                    (isParentRow && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT)) ||
                    (!isParentRow && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER))
                ) {
                    const { checked, disabled } = getCheckboxState(rowData, isParentRow);
                    renderComponent = (
                        <input
                            type="checkbox"
                            className={styles.Checkbox}
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) => {
                                e.stopPropagation();
                                if (isParentRow) {
                                    updateProductTerms(rowData.productId);
                                } else {
                                    if (rowData.lenderId) {
                                        updateLenderTerms(rowData.lenderId, rowData.productId);
                                    }
                                }
                            }}
                        />
                    );
                } else {
                    renderComponent = <></>;
                }
                break;
            case ENTITY_TYPE_KEYS.CARET:
                if (isParentRow && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
                    renderComponent = (
                        <div
                            className={clsx(styles.CaretDiv, {
                                [styles.CaretDivHov]: rowData.expand,
                            })}
                        >
                            <img
                                className={clsx(styles.CaretImg, {
                                    [styles.ExpandedCaret]: rowData?.expand && isParentRow,
                                    [styles.DisabledCaret]: !rowData?.lenders?.length,
                                })}
                                src={caretRight}
                                height={'14px'}
                                alt={''}
                                style={{ marginTop: '4px' }}
                            />
                        </div>
                    );
                }
                break;
            case ENTITY_TYPE_KEYS.PROBABILITY: {
                const isCalculating = isRowCalculating(rowData, isParentRow);
                const probabilityRow = (
                    <div className={styles.ProbabilityRow} style={{ opacity: 1 }}>
                        {isCalculating ? (
                            <AIText text="Calculating..." style={{ fontSize: '12px' }} />
                        ) : (
                            <>
                                <ProbabilityBar
                                    probability={rowData?.[headerId]}
                                    pecThreshold={rowData?.pecValues?.PEC_THRESHOLD_PERCENTAGE}
                                />
                                <img src={ctaGrey} alt={''} height={'12px'} />
                            </>
                        )}
                    </div>
                );
                const tooltipData = getProbabilityTooltip(rowData?.[headerId], rowData?.pecValues?.PEC_THRESHOLD_PERCENTAGE);

                renderComponent = tooltipData ? (
                    <Tooltip
                        content={
                            <div className={probabilityBarStyles.TooltipContent}>
                                <span className={probabilityBarStyles.TooltipText}>{tooltipData.message}</span>
                                {tooltipData.recommendedPercentage && (
                                    <span className={probabilityBarStyles.TooltipRecommended}>
                                        Recommended: {tooltipData.recommendedPercentage}
                                    </span>
                                )}
                            </div>
                        }
                        direction="top"
                        usePortal={true}
                        style={{ width: '200px', maxWidth: '250px' }}
                        containerFullWidth={false}
                    >
                        {probabilityRow}
                    </Tooltip>
                ) : (
                    probabilityRow
                );
                break;
            }
            case ENTITY_TYPE_KEYS.SECURITY: {
                const totalElements = rowData?.security?.length;
                renderComponent = (
                    <Tooltip content={getTooltipContent(rowData?.security, styles)} direction={'top'} fitContent>
                        <div
                            className={clsx(styles.SecurityContainer, {
                                [styles.SecurityContainerDisabled]: isFieldDisabled,
                            })}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isFieldDisabled) return;
                                setShowSecurityModal({
                                    securityData: rowData?.security,
                                    productId: rowData?.productId,
                                    productName: isParentRow ? rowData?.productName : rowData?.lenderName || '',
                                    lenderId: rowData?.lenderId,
                                    isParentRow,
                                    pecSecurity: rowData?.pecValues?.PEC_SECURITY as string[][] | undefined,
                                });
                            }}
                            style={{ cursor: isFieldDisabled ? 'default' : 'pointer' }}
                        >
                            {rowData?.security?.slice(0, 1)?.map((sec, i) =>
                                !!sec?.type ? (
                                    <div
                                        key={i}
                                        className={clsx(styles.SecurityItem, {
                                            [styles.SecurityItemDisabled]: isFieldDisabled,
                                        })}
                                    >
                                        {sec.type}
                                    </div>
                                ) : (
                                    '-'
                                ),
                            ) || '-'}
                            {totalElements > 1 && <div className={styles.More}>+{totalElements - 1} more</div>}
                        </div>
                    </Tooltip>
                );
                break;
            }
            case PRODUCT_TERMS_KEYS.AMOUNT:
            case PRODUCT_TERMS_KEYS.TENURE:
            case PRODUCT_TERMS_KEYS.ROI: {
                const fieldConfig = getEditableFieldConfig(headerId);
                const isCalculating = isRowCalculating(rowData, isParentRow);
                if (fieldConfig) {
                    // Disable if entry exists in productTerms/lenderTerms and isShared is not 0

                    // Get original value key mapping
                    const originalKeyMap: Record<string, string> = {
                        amount: 'originalAmount',
                        tenure: 'originalTenure',
                        roi: 'originalRoi',
                    };
                    const originalKey = originalKeyMap[headerId];
                    const originalValue = rowData?.[originalKey];

                    // Format original value with unit
                    const formatOriginalValue = () => {
                        if (originalValue === undefined || originalValue === null) return null;
                        if (headerId === PRODUCT_TERMS_KEYS.AMOUNT) {
                            return `${fieldConfig.unit}${originalValue}${fieldConfig.secondaryUnit || ''}`;
                        }
                        return `${originalValue}${fieldConfig.unit}`;
                    };

                    const formattedOriginalValue = formatOriginalValue();

                    // Format current value for AIText display
                    const formatCurrentValue = () => {
                        const value = rowData?.[headerId];
                        if (isEmpty(value)) return '';
                        if (headerId === PRODUCT_TERMS_KEYS.AMOUNT) {
                            return `${fieldConfig.unit}${value}${fieldConfig.secondaryUnit || ''}`;
                        }
                        return `${value}${fieldConfig.unit}`;
                    };

                    renderComponent = isCalculating ? (
                        <AIText
                            text={formatCurrentValue()}
                            animate={true}
                            style={{
                                background: 'linear-gradient(90deg, #FFFFFF -13.76%, #4D4D4D 26.14%, #4D4D4D 93.76%)',
                                backgroundSize: '200% 200%',
                                WebkitTextFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                fontSize: '14px',
                            }}
                        />
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                                className={clsx({
                                    [styles.Strikethrough]: isLenderStrikethrough,
                                    [styles.GrayedText]: isLenderStrikethrough,
                                })}
                            >
                                <NumberCellRenderer
                                    column={{
                                        field: fieldConfig.field,
                                        headerLabel: fieldConfig.headerLabel,
                                        headerOriginalLabel: fieldConfig.headerLabel,
                                        type: DataTableColumnType.NUMBER,
                                        unit: fieldConfig.unit,
                                        unitLeft: fieldConfig.unitLeft,
                                        secondaryUnit: fieldConfig.secondaryUnit,
                                        allowDecimal: fieldConfig.allowDecimal,
                                    }}
                                    row={{ id: rowData?.productId || rowData?.lenderId }}
                                    value={rowData?.[headerId]}
                                    rowIndex={0}
                                    columnIndex={0}
                                    dynamicProps={{
                                        updateCustomFieldStatus: (row, update, column, handlers) => {
                                            if (isEmpty(update?.value)) return;
                                            updateEditableField(fieldConfig.field, update.value, rowData, isParentRow, handlers);
                                        },
                                        isDisabled: !!isFieldDisabled,
                                    }}
                                />
                            </span>
                            {formattedOriginalValue && !isLenderStrikethrough && (
                                <span className={clsx(styles.OriginalValueContainer, styles.Strikethrough, styles.GrayedText)}>
                                    {formattedOriginalValue}
                                </span>
                            )}
                        </div>
                    );
                }
                break;
            }

            default:
                renderComponent = rowData?.[headerId] ?? '-';
        }

        return (
            <td
                key={header.id}
                className={clsx({
                    [styles.RowItem]: true,
                    [styles.SelectColInner]: headerId === ENTITY_TYPE_KEYS.SELECT && !isParentRow,
                    [styles.SelectCol]: headerId === ENTITY_TYPE_KEYS.SELECT && isParentRow,
                    [styles.CaretCol]: headerId === ENTITY_TYPE_KEYS.CARET && isParentRow,
                    [styles.CaretColInner]: headerId === ENTITY_TYPE_KEYS.CARET && !isParentRow,
                    [styles.ProductsCol]: headerId === ENTITY_TYPE_KEYS.PRODUCT,
                    [styles.ProbabilityCol]: headerId === ENTITY_TYPE_KEYS.PROBABILITY,
                    [styles.SecurityCol]: headerId === ENTITY_TYPE_KEYS.SECURITY,
                    [styles.DisabledRowItem]: isParentRow && !rowData?.lenders?.length,
                    [styles.NoSelectMode]:
                        headerId === ENTITY_TYPE_KEYS.SELECT &&
                        ((isParentRow && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT)) ||
                            (!isParentRow && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER))),
                })}
                onClick={(e) => {
                    switch (headerId) {
                        case ENTITY_TYPE_KEYS.CARET:
                            e.stopPropagation();
                            if (isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
                                expandOrCollapseRow(rowData, isParentRow);
                            }
                            break;
                        case ENTITY_TYPE_KEYS.PROBABILITY:
                            e.stopPropagation();
                            setShowDetailOverview(rowData);
                            registerMixpanelForUserHelpClick(rowData, isParentRow);
                            break;
                        case ENTITY_TYPE_KEYS.SELECT:
                            e.stopPropagation();
                            break;
                        default:
                            break;
                    }
                }}
            >
                {renderComponent}
            </td>
        );
    };

    const expandOrCollapseRow = (rowData, isParentRow) => {
        if (isParentRow && rowData?.lenders?.length && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
            if (!rowData.expand) {
                registerMixpanelForRowExpand(rowData);
            }
            dispatch(toggleProductExpand(rowData.productId));
        }
    };

    return (
        <>
            <div
                className={clsx({
                    [styles.DebtProducts]: true,
                    [styles.DebtProductsCompany]: isCompanySide,
                    [styles.NoAccordion]: !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION),
                    [styles.NoSelectProduct]: !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT),
                    [styles.NoSelectLender]: !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER),
                })}
            >
                <div className={styles.Container}>
                    <div className={styles.Top}>
                        <div className={styles.TitleSection}>
                            <div className={styles.Title}>{debtProductsData.visibilityStates.debtProductName ?? 'Debt Products'}</div>
                            <MandateDropdown
                                data={draftDealsData}
                                dealStatusConfig={dealStatusConfig}
                                selectedMandate={selectedMandate}
                                placeholder="Select Mandate"
                                onSelect={(mandate) => {
                                    handleActionWithUnsavedCheck(() => handleMandateSelection(mandate));
                                }}
                                onClear={() => handleActionWithUnsavedCheck(() => handleMandateSelection(null))}
                                className={styles.MandateDropdown}
                                disabled={isEmpty(draftDealsData)}
                            />
                        </div>
                        <div className={styles.Right}>
                            {lastUpdatedInfo && (
                                <span style={{ fontSize: '0.875rem', color: '#8D8D8D', marginRight: '1rem', whiteSpace: 'nowrap' }}>
                                    {lastUpdatedInfo.prefix} <strong style={{ color: '#4D4D4D' }}>{lastUpdatedInfo.highlight}</strong>
                                </span>
                            )}
                            {showFilters && (
                                <Button
                                    text={isEmpty(debtFilters) ? 'Add Filters' : 'Filters Applied'}
                                    onClick={() => {
                                        handleActionWithUnsavedCheck(() => {
                                            dispatch(
                                                setDealDrawer({
                                                    open: true,
                                                    drawerLabel: 'Filters',
                                                    drawerSource: 'Debt Products Filters',
                                                }),
                                            );
                                        });
                                    }}
                                    icon={debtProdFilters}
                                    style={
                                        isEmpty(debtFilters)
                                            ? { marginTop: '0' }
                                            : {
                                                  marginTop: 0,
                                                  background: '#F2E8FF',
                                                  border: '1px solid transparent',
                                              }
                                    }
                                    variant={'purpleinverted'}
                                    endIcon={isEmpty(debtFilters) ? null : PURPLE_CROSS}
                                    endIconClick={() => {
                                        handleActionWithUnsavedCheck(() => {
                                            dispatch(setDebtProductsFilters({}));
                                            fetchDebtProductsData({});
                                        });
                                    }}
                                />
                            )}
                            {isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.EDIT_PROFILE) && (
                                <Button
                                    text={
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.75rem',
                                                width: '100%',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <span>Edit Profile</span>
                                            {percentageCompletion?.borrowerFinancials?.actualData !== undefined && (
                                                <div className={styles.PercentageCompletion}>
                                                    {percentageCompletion?.borrowerFinancials?.actualData}%
                                                </div>
                                            )}
                                        </div>
                                    }
                                    onClick={() => {
                                        handleActionWithUnsavedCheck(() => {
                                            dispatch(
                                                setDealDrawer({
                                                    open: true,
                                                    drawerLabel: 'Edit Profile',
                                                    companyName: selectedCompany?.orgName,
                                                }),
                                            );
                                            registerMixpanelForEditProfileClicked('Kebab Menu');
                                        });
                                    }}
                                    variant="purple"
                                    style={{ marginTop: '0' }}
                                    icon={editProfileIcon}
                                />
                            )}
                        </div>
                    </div>

                    <div className={styles.Body}>
                        <div
                            // style={selectionCount > 0 || draftSelectionCount > 0 ? { height: 'calc(100vh - 280px)' } : {}}
                            className={styles.TableContainer}
                        >
                            <table className={styles.Table}>
                                <thead>
                                    <tr className={styles.HeadersRow}>
                                        {debtProductsData?.headers?.length ? (
                                            debtProductsData?.headers
                                                .filter((header) => {
                                                    if (
                                                        header.id === ENTITY_TYPE_KEYS.CARET &&
                                                        !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)
                                                    ) {
                                                        return false;
                                                    }
                                                    if (
                                                        header.id === ENTITY_TYPE_KEYS.SELECT &&
                                                        !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT)
                                                    ) {
                                                        return false;
                                                    }
                                                    return true;
                                                })
                                                .map((header) => (
                                                    <th
                                                        key={header?.id}
                                                        className={clsx({
                                                            [styles.Header]: true,
                                                            [styles.SelectCol]: header.id === ENTITY_TYPE_KEYS.SELECT,
                                                            [styles.CaretCol]: header.id === ENTITY_TYPE_KEYS.CARET,
                                                            [styles.ProductsCol]: header.id === ENTITY_TYPE_KEYS.PRODUCT,
                                                            [styles.ProbabilityCol]: header.id === ENTITY_TYPE_KEYS.PROBABILITY,
                                                            [styles.SecurityCol]: header.id === ENTITY_TYPE_KEYS.SECURITY,
                                                            [styles.NoAccordionMode]: !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION),
                                                        })}
                                                    >
                                                        {header.label}
                                                    </th>
                                                ))
                                        ) : (
                                            <th className={clsx(styles.Header, styles.EmptyHeader)}>{'-'}</th>
                                        )}
                                    </tr>
                                </thead>
                                {!loading && !showEmptyScreen && (
                                    <tbody className={styles.TableBody}>
                                        {debtProductsData?.rows?.map((rowData: DebtProductRowData, i) => (
                                            <React.Fragment key={i}>
                                                <tr
                                                    key={`${i}-${rowData?.productId}`}
                                                    className={clsx(styles.TableRow, styles.ParentRow, {
                                                        [styles.ExpandedRow]: rowData?.expand,
                                                        [styles.NoAccordionMode]: !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION),
                                                    })}
                                                    onClick={() => {
                                                        if (isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
                                                            expandOrCollapseRow(rowData, true);
                                                            registerMixpanelForUserHelpClick(rowData, true);
                                                        }
                                                    }}
                                                >
                                                    {debtProductsData?.headers
                                                        .filter((header) => {
                                                            if (
                                                                header.id === ENTITY_TYPE_KEYS.CARET &&
                                                                !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)
                                                            ) {
                                                                return false;
                                                            }
                                                            if (
                                                                header.id === ENTITY_TYPE_KEYS.SELECT &&
                                                                !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT)
                                                            ) {
                                                                return false;
                                                            }

                                                            return true;
                                                        })
                                                        .map((header) => (
                                                            <React.Fragment key={header.id}>
                                                                {renderRowItem(header.id, rowData, true)}
                                                            </React.Fragment>
                                                        ))}
                                                </tr>
                                                {rowData?.expand &&
                                                    rowData?.lenders?.map((lenderData: DebtProductRowData, j) => (
                                                        <tr
                                                            key={j}
                                                            className={clsx(styles.TableRow, styles.Subrow, {
                                                                [styles.NoAccordionMode]: !isVisible(
                                                                    VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION,
                                                                ),
                                                            })}
                                                            onClick={() => {
                                                                setShowDetailOverview(lenderData);
                                                                registerMixpanelForUserHelpClick(rowData, false);
                                                            }}
                                                        >
                                                            {debtProductsData?.headers
                                                                .filter((header) => {
                                                                    if (
                                                                        header.id === ENTITY_TYPE_KEYS.CARET &&
                                                                        !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)
                                                                    ) {
                                                                        return false;
                                                                    }
                                                                    if (
                                                                        header.id === ENTITY_TYPE_KEYS.SELECT &&
                                                                        !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER)
                                                                    ) {
                                                                        return false;
                                                                    }

                                                                    return true;
                                                                })
                                                                .map((header) => (
                                                                    <React.Fragment key={header.id}>
                                                                        {renderRowItem(header.id, lenderData, false)}
                                                                    </React.Fragment>
                                                                ))}
                                                        </tr>
                                                    ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                )}
                            </table>
                            {loading && showLoader()}
                            {!loading && showEmptyScreen && renderEmptyScreen()}
                        </div>
                    </div>
                </div>

                {showDetailOverview && (
                    <DebtProductOverview
                        data={showDetailOverview}
                        closeModal={() => setShowDetailOverview(null)}
                        percentageCompletion={percentageCompletion}
                        selectedCompany={selectedCompany}
                        registerMixpanelForEditProfileClicked={registerMixpanelForEditProfileClicked}
                    />
                )}
            </div>
            {!isEmpty(debtProductsData.rows) && (
                <div
                    className={clsx(styles.Footer, {
                        [styles.FooterShift]: commentsThreadState.showCommentsThread && !commentsThreadState.closingCommentsThread,
                    })}
                    style={{
                        ...(!isEmpty(productTerms) || !isEmpty(lenderTerms)
                            ? { bottom: '0', opacity: '1', height: 'auto', visibility: 'visible' }
                            : {}),
                        ...(isCompanySide ? { left: '2.125rem' } : {}),
                    }}
                >
                    {isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.CREATE_DEAL) && (
                        <div className={styles.FooterText}>
                            <MandateAggregatedTermsComponent
                                aggregatedMandateTerms={aggregatedMandateTerms}
                                className={!isEmpty(productTerms) || !isEmpty(lenderTerms) ? mandateTermsStyles.animate : ''}
                            />
                            <div className={styles.FooterBtnContainer}>
                                {!savingDraft && renderClearOrDeleteButton(dealId)}
                                {savingDraft ? <AIText text={'Saving and adding it on Mandate'} /> : renderDealActionButton()}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <AskAicaV2 selectedCompany={selectedCompany} global />

            {showSuccessModal && (
                <DealSuccessModal
                    showSuccessModal={showSuccessModal}
                    heading="Mandate Created Successfully"
                    subHeading="You can share deal with lenders now."
                    buttonText={ButtonText.VIEW_MANDATE}
                    buttonOnClick={() =>
                        navigate(NAVIGATION_CONSTANTS.COMPANY_DEALS_WITH_MANDATE_UPDATED(assesseeOrgId), {
                            state: {
                                ...{ from: 'SCAN' },
                            },
                        })
                    }
                    queryParamsToClearOnClose={['createMandate']}
                    showCrossButton={true}
                    onCLose={() => {}}
                />
            )}
            {dealDrawerState.open && (
                <ManageDealDrawer
                    source={dealDrawerState?.drawerSource ?? 'Debt Products'}
                    onClose={(payload) => {
                        fetchPECDataAndDebtProductsData(payload);
                        const isEditProfile = dealDrawerState?.drawerLabel === 'Edit Profile';
                        fetchCompanyMetrics(false, isEditProfile);
                    }}
                    dealId={dealId}
                    saveSelection={saveSelection}
                    fetchCompanyMetrics={fetchCompanyMetrics}
                    dealStatus={dealStatus}
                />
            )}

            {isMandateSharingDrawerOpen && (
                <MandateSharingDrawer
                    isOpen={isMandateSharingDrawerOpen}
                    onClose={() => setIsMandateSharingDrawerOpen(false)}
                    onShareWithLender={() => setIsShareWithLenderModalOpen(true)}
                    onEndUseOfCapitalChange={updateEndUseOfCapital}
                    initialEndUseOfCapital={dealTerms?.EndUseofCapital || ''}
                    dealId={dealId}
                    onUpdateSelectedUsers={updateSelectedUsers}
                    selectedUsers={selectedUsers}
                />
            )}

            {isShareWithLenderModalOpen && (
                <ShareWithLenderModal
                    open={isShareWithLenderModalOpen}
                    onClose={() => {
                        setIsShareWithLenderModalOpen(false);
                    }}
                    onSend={() => saveSelection(DealPayloadScenario.UPDATE_NOTES_DOCS)}
                    isTransitioning={isTransitioning}
                    hideContent={isTransitioning}
                    assesseeOrgId={assesseeOrgId}
                    addDealNote={addDealNote}
                    updateDealDocuments={updateDealDocuments}
                    companyName={selectedCompany?.orgName}
                />
            )}

            {showShareSuccessModal && (
                <DealSuccessModal
                    showSuccessModal={showShareSuccessModal}
                    heading="Deal Shared Successfully"
                    subHeading="Your mandate has been shared with the selected lenders"
                    buttonText={ButtonText.VIEW_DEALS}
                    buttonOnClick={() => {
                        setShowShareSuccessModal(false);
                        if (assesseeOrgId) {
                            navigate(NAVIGATION_CONSTANTS.COMPANY_DEALS_WITH_DEAL_UPDATED(assesseeOrgId), {
                                state: { fromDealSuccess: true },
                            });
                        } else {
                            navigate(NAVIGATION_CONSTANTS.DEALS_WITH_DEAL_UPDATED, {
                                state: { fromDealSuccess: true },
                            });
                        }
                    }}
                    showCrossButton={false}
                    onCLose={() => {
                        setShowShareSuccessModal(false);
                    }}
                    showContent={showShareSuccessModal}
                />
            )}

            {showSecurityModal && (
                <SecurityModal
                    open={!!showSecurityModal}
                    onClose={() => setShowSecurityModal(null)}
                    modalData={showSecurityModal}
                    onSave={updateSecurityOffered}
                />
            )}

            {showUnsavedChangesModal && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.CREATE_DEAL) && (
                <UnsavedChangesModal
                    isOpen={showUnsavedChangesModal}
                    onClose={handleCloseUnsavedModal}
                    onCloseWithoutSaving={handleCloseWithoutSaving}
                    onSaveChanges={() => handleSaveChanges(() => saveSelection(DealPayloadScenario.UPDATE_BASE))}
                />
            )}
        </>
    );
}

export default DebtProducts;
